import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type CategoryPageItem = {
  id: string;
  category_id: string;
  position: number;
  question: string;
  answer: string;
  group_name: string | null;
};

export type CategoryPage = {
  category_id: string;
  intro_title: string | null;
  intro_body: string | null;
  seo_title: string | null;
  seo_description: string | null;
  items: CategoryPageItem[];
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const slugSchema = z.object({ slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/) });

export const getCategoryPageBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => slugSchema.parse(input))
  .handler(async ({ data }): Promise<CategoryPage | null> => {
    const sb = publicClient();
    const { data: catRow, error: catErr } = await sb
      .from("categories")
      .select("id")
      .eq("slug", data.slug)
      .eq("kind", "page")
      .limit(1)
      .maybeSingle();
    if (catErr) throw new Error(catErr.message);
    if (catRow) return await loadPage(sb, catRow.id);
    const { data: slideRow, error: slideErr } = await sb
      .from("slides")
      .select("category_id")
      .eq("category_slug", data.slug)
      .eq("category_kind", "page")
      .limit(1)
      .maybeSingle();
    if (slideErr) throw new Error(slideErr.message);
    if (!slideRow) return null;
    return await loadPage(sb, slideRow.category_id);
  });

const catIdSchema = z.object({ category_id: z.string().min(1).max(100) });

export const getAllCategoryPages = createServerFn({ method: "GET" }).handler(
  async (): Promise<CategoryPage[]> => {
    const sb = publicClient();
    const [{ data: pages, error: pErr }, { data: items, error: iErr }] = await Promise.all([
      sb
        .from("category_pages")
        .select("category_id,intro_title,intro_body,seo_title,seo_description"),
      sb
        .from("category_page_items")
        .select("id,category_id,position,question,answer,group_name")
        .order("position", { ascending: true }),
    ]);
    if (pErr) throw new Error(pErr.message);
    if (iErr) throw new Error(iErr.message);
    const itemsByCat = new Map<string, CategoryPageItem[]>();
    for (const it of (items ?? []) as CategoryPageItem[]) {
      const arr = itemsByCat.get(it.category_id) ?? [];
      arr.push(it);
      itemsByCat.set(it.category_id, arr);
    }
    return (pages ?? []).map((p) => ({
      category_id: p.category_id,
      intro_title: p.intro_title,
      intro_body: p.intro_body,
      seo_title: p.seo_title,
      seo_description: p.seo_description,
      items: itemsByCat.get(p.category_id) ?? [],
    }));
  },
);

export const getCategoryPageById = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => catIdSchema.parse(input))
  .handler(async ({ data }): Promise<CategoryPage | null> => {
    const sb = publicClient();
    return await loadPage(sb, data.category_id);
  });

async function loadPage(
  sb: ReturnType<typeof publicClient>,
  category_id: string,
): Promise<CategoryPage | null> {
  const [{ data: page, error: pageErr }, { data: items, error: itemsErr }] = await Promise.all([
    sb
      .from("category_pages")
      .select("category_id,intro_title,intro_body,seo_title,seo_description")
      .eq("category_id", category_id)
      .maybeSingle(),
    sb
      .from("category_page_items")
      .select("id,category_id,position,question,answer,group_name")
      .eq("category_id", category_id)
      .order("position", { ascending: true }),
  ]);
  if (pageErr) throw new Error(pageErr.message);
  if (itemsErr) throw new Error(itemsErr.message);
  return {
    category_id,
    intro_title: page?.intro_title ?? null,
    intro_body: page?.intro_body ?? null,
    seo_title: page?.seo_title ?? null,
    seo_description: page?.seo_description ?? null,
    items: (items ?? []) as CategoryPageItem[],
  };
}

const upsertPageSchema = z.object({
  category_id: z.string().min(1).max(100),
  intro_title: z.string().max(200).nullable(),
  intro_body: z.string().max(2000).nullable(),
  seo_title: z.string().max(200).nullable(),
  seo_description: z.string().max(500).nullable(),
});

export const upsertCategoryPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => upsertPageSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("category_pages")
      .upsert(
        {
          category_id: data.category_id,
          intro_title: data.intro_title,
          intro_body: data.intro_body,
          seo_title: data.seo_title,
          seo_description: data.seo_description,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "category_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const addItemSchema = z.object({ category_id: z.string().min(1).max(100) });

export const addPageItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => addItemSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: existing, error: exErr } = await supabase
      .from("category_page_items")
      .select("position")
      .eq("category_id", data.category_id)
      .order("position", { ascending: false })
      .limit(1);
    if (exErr) throw new Error(exErr.message);
    const nextPos = ((existing?.[0]?.position ?? -1) as number) + 1;
    const { error } = await supabase.from("category_page_items").insert({
      category_id: data.category_id,
      position: nextPos,
      question: "New question",
      answer: "Answer goes here.",
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const updateItemSchema = z.object({
  id: z.string().uuid(),
  question: z.string().min(1).max(500),
  answer: z.string().min(1).max(4000),
  group_name: z.string().max(100).nullable().optional(),
});

export const updatePageItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => updateItemSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("category_page_items")
      .update({
        question: data.question,
        answer: data.answer,
        group_name: data.group_name ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const deleteItemSchema = z.object({ id: z.string().uuid() });

export const deletePageItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => deleteItemSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("category_page_items")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const reorderItemsSchema = z.object({
  category_id: z.string().min(1).max(100),
  ordered_ids: z.array(z.string().uuid()).min(1).max(200),
});

export const reorderPageItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => reorderItemsSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await supabase
        .from("category_page_items")
        .update({ position: -1000 - i })
        .eq("id", data.ordered_ids[i])
        .eq("category_id", data.category_id);
      if (error) throw new Error(error.message);
    }
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await supabase
        .from("category_page_items")
        .update({ position: i, updated_at: new Date().toISOString() })
        .eq("id", data.ordered_ids[i])
        .eq("category_id", data.category_id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });
